import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Check, Loader2, XCircle } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_POLL_ATTEMPTS = 20; // ~40s

export const PaymentSuccess = () => {
  const [params] = useSearchParams();
  const { language } = useLanguage();
  const sessionId = params.get('session_id');
  const [status, setStatus] = useState('polling');
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      return;
    }
    let cancelled = false;
    let timer;

    const poll = async () => {
      try {
        const res = await axios.get(`${API_URL}/payments/status/${sessionId}`);
        if (cancelled) return;
        if (res.data.payment_status === 'paid') {
          setStatus('paid');
          return;
        }
        if (res.data.payment_status === 'failed' || res.data.status === 'expired') {
          setStatus('failed');
          return;
        }
        if (attempts >= MAX_POLL_ATTEMPTS) {
          setStatus('timeout');
          return;
        }
        setAttempts((a) => a + 1);
        timer = setTimeout(poll, 2000);
      } catch (e) {
        if (!cancelled) setStatus('error');
      }
    };
    poll();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-4 bg-gradient-to-br from-emerald-50 to-teal-50" data-testid="payment-success-page">
      <div className="max-w-md w-full bg-white rounded-3xl shadow-2xl p-8 sm:p-10 text-center animate-scale-in">
        {(status === 'polling') && (
          <>
            <Loader2 className="w-16 h-16 text-emerald-600 mx-auto mb-4 animate-spin" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              {language === 'fr' ? 'Confirmation en cours...' : 'Confirming...'}
            </h1>
            <p className="text-gray-600">
              {language === 'fr' ? 'Nous validons votre paiement.' : 'Validating your payment.'}
            </p>
          </>
        )}
        {status === 'paid' && (
          <>
            <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Check className="w-12 h-12 text-emerald-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-3">
              {language === 'fr' ? 'Bienvenue Premium !' : 'Welcome Premium!'}
            </h1>
            <p className="text-gray-600 mb-6">
              {language === 'fr'
                ? 'Votre essai gratuit de 72h a démarré. Profitez pleinement de ZéphyrBreath !'
                : 'Your 72h free trial has started. Enjoy ZéphyrBreath!'}
            </p>
            <Link to="/dashboard">
              <Button className="w-full h-12 rounded-full bg-emerald-600 hover:bg-emerald-700 shadow-lg" data-testid="go-to-dashboard-btn">
                {language === 'fr' ? 'Accéder au dashboard' : 'Go to dashboard'}
              </Button>
            </Link>
          </>
        )}
        {(status === 'failed' || status === 'error' || status === 'timeout') && (
          <>
            <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              {language === 'fr' ? 'Confirmation impossible' : 'Confirmation failed'}
            </h1>
            <p className="text-gray-600 mb-6">
              {language === 'fr'
                ? 'Nous n\'avons pas pu confirmer votre paiement. Contactez le support si le montant a été débité.'
                : 'We could not confirm your payment. Contact support if you were charged.'}
            </p>
            <Link to="/subscription">
              <Button variant="outline" className="w-full h-12 rounded-full">
                {language === 'fr' ? 'Retour aux plans' : 'Back to plans'}
              </Button>
            </Link>
          </>
        )}
      </div>
    </div>
  );
};

export const PaymentCancel = () => {
  const { language } = useLanguage();
  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-4" data-testid="payment-cancel-page">
      <div className="max-w-md w-full bg-white rounded-3xl shadow-xl p-8 sm:p-10 text-center">
        <XCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
        <h1 className="text-2xl font-bold text-gray-900 mb-2">
          {language === 'fr' ? 'Paiement annulé' : 'Payment cancelled'}
        </h1>
        <p className="text-gray-600 mb-6">
          {language === 'fr'
            ? 'Aucun montant n\'a été prélevé. Vous pouvez réessayer à tout moment.'
            : 'No amount was charged. You can try again anytime.'}
        </p>
        <div className="flex flex-col gap-3">
          <Link to="/subscription">
            <Button className="w-full h-12 rounded-full bg-emerald-600 hover:bg-emerald-700">
              {language === 'fr' ? 'Revoir les plans' : 'Review plans'}
            </Button>
          </Link>
          <Link to="/dashboard">
            <Button variant="outline" className="w-full h-12 rounded-full">
              {language === 'fr' ? 'Retour au dashboard' : 'Back to dashboard'}
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};
